import {
  ArrowRightIcon,
  CalendarDaysIcon,
  SendIcon,
  SparklesIcon,
  UsersIcon,
  type LucideIcon,
} from "lucide-react";

import { useEffect, useRef } from "react";

import { useI18n, type MessageKey } from "~/i18n";
import { MorrowMascot } from "../brand/MorrowMascot";
import { Button } from "../ui/button";
import { useMorrowClerkAuth } from "./morrowClerkAuth";

/**
 * Concrete outputs Morrow delivers — reach customers, publish on social, and
 * schedule meetings. Shown as action cards so visitors see what they'll get, not
 * which platform features exist.
 */
const AUTH_WELCOME_OUTCOMES: ReadonlyArray<{
  icon: LucideIcon;
  titleKey: MessageKey;
  descKey: MessageKey;
}> = [
  {
    icon: UsersIcon,
    titleKey: "authWelcome.outcome.briefing.title",
    descKey: "authWelcome.outcome.briefing.desc",
  },
  {
    icon: SendIcon,
    titleKey: "authWelcome.outcome.pipeline.title",
    descKey: "authWelcome.outcome.pipeline.desc",
  },
  {
    icon: CalendarDaysIcon,
    titleKey: "authWelcome.outcome.align.title",
    descKey: "authWelcome.outcome.align.desc",
  },
];

/**
 * Front-door greeting for signed-out visitors on the empty draft — the "greeting
 * WITH sign-in" first step. Morrow introduces itself, shows what it can do, and
 * offers a single sign-in call to action that opens Clerk's modal (via the shared
 * `openSignIn`, so there is exactly ONE sign-in surface — no stacked card when a
 * guest also clicks a signed-in-only nav item). After they authenticate, ChatView
 * continues straight into onboarding (account type → form → tools → greeting).
 */
export function MorrowAuthWelcome() {
  const { t } = useI18n();
  const { isLoaded, isSignedIn, openSignIn, openSignUp } = useMorrowClerkAuth();

  // Organization-invitation landing: the email link brought a `__clerk_ticket`.
  // Auto-open Clerk's modal, which reads the ticket from the URL and completes the
  // invitation in-app (ChatView then continues into onboarding) — instead of the
  // invitee bouncing to Clerk's hosted Account Portal. Clerk sets `__clerk_status`
  // to tell us which surface the ticket needs: `sign_in` when the invitee already
  // has a Morrow account, otherwise sign-up for a brand-new invitee. Fires once.
  const ticketConsumeStarted = useRef(false);
  useEffect(() => {
    if (ticketConsumeStarted.current || !isLoaded || isSignedIn) return;
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (!params.has("__clerk_ticket")) return;
    ticketConsumeStarted.current = true;
    if (params.get("__clerk_status") === "sign_in") openSignIn();
    else openSignUp();
  }, [isLoaded, isSignedIn, openSignIn, openSignUp]);

  if (isSignedIn) return null;

  // Hold the slot with just the mascot while Clerk hydrates so a returning
  // session doesn't flash the sign-in CTA before flipping to onboarding.
  if (!isLoaded) {
    return (
      <div className="mx-auto flex w-full max-w-lg flex-col items-center gap-6 text-center">
        <MorrowMascot className="size-16 opacity-60 sm:size-20" />
      </div>
    );
  }

  return (
    <div className="mx-auto grid w-full max-w-4xl items-center gap-x-10 gap-y-8 py-4 sm:grid-cols-[1.05fr_0.95fr]">
      {/* Left column — who Morrow is and what it delivers for company teams. */}
      <div className="flex flex-col items-center gap-5 text-center sm:items-start sm:text-left">
        <div className="flex items-center gap-3">
          <div className="relative shrink-0">
            {/* Soft glow behind the mascot to give the hero a little lift. */}
            <div
              aria-hidden
              className="absolute inset-0 -z-10 scale-150 rounded-full bg-primary/25 blur-2xl"
            />
            <MorrowMascot className="size-12 sm:size-14" />
          </div>
          <h2 className="text-2xl font-semibold tracking-[-0.02em] text-foreground sm:text-3xl">
            {t("authWelcome.title")}
          </h2>
        </div>
        <p className="max-w-md text-balance text-sm leading-relaxed text-muted-foreground sm:text-base">
          {t("authWelcome.subtitle")}
        </p>
        <div className="flex w-full flex-col gap-2.5 text-left">
          <p className="text-xs font-medium uppercase tracking-[0.08em] text-muted-foreground/80">
            {t("authWelcome.outcomesHeading")}
          </p>
          <ul className="flex w-full flex-col gap-2">
            {AUTH_WELCOME_OUTCOMES.map((outcome) => {
              const Icon = outcome.icon;
              return (
                <li
                  key={outcome.titleKey}
                  className="flex items-start gap-3 rounded-xl border border-border/50 bg-card/30 px-3.5 py-2.5 transition-colors hover:border-border hover:bg-card/60"
                >
                  <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg border border-border/70 bg-background/70 text-primary">
                    <Icon className="size-4" />
                  </span>
                  <span className="flex min-w-0 flex-col gap-0.5">
                    <span className="text-sm font-medium text-foreground">
                      {t(outcome.titleKey)}
                    </span>
                    <span className="text-xs leading-snug text-muted-foreground/85">
                      {t(outcome.descKey)}
                    </span>
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      {/* Right column — the free-trial CTA as a raised card (the single,
          unmissable "let's go" surface). Primary action starts the trial via
          sign-up; a quiet secondary link routes returning users to sign-in. */}
      <div className="relative w-full">
        <div
          aria-hidden
          className="absolute -inset-px -z-10 rounded-3xl bg-gradient-to-b from-primary/40 via-primary/10 to-transparent blur-[2px]"
        />
        <div className="flex flex-col items-center gap-4 rounded-3xl border border-primary/25 bg-gradient-to-b from-primary/[0.08] to-card/60 px-6 py-6 text-center shadow-xl shadow-primary/10 backdrop-blur-sm">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
            <SparklesIcon className="size-3.5" />
            {t("authWelcome.trial.badge")}
          </span>
          <p className="text-balance text-base font-semibold leading-snug text-foreground">
            {t("authWelcome.trial.tagline")}
          </p>
          <Button className="group w-full gap-2 text-base" size="lg" onClick={() => openSignUp()}>
            {t("authWelcome.cta")}
            <ArrowRightIcon className="size-4 transition-transform group-hover:translate-x-0.5" />
          </Button>
          <p className="text-[0.7rem] leading-snug text-muted-foreground/70">
            {t("authWelcome.reassure")}
          </p>
          <div className="h-px w-full bg-border/60" />
          <p className="text-xs text-muted-foreground">
            {t("authWelcome.signin.prompt")}{" "}
            <button
              type="button"
              className="font-medium text-primary underline-offset-4 hover:underline"
              onClick={() => openSignIn()}
            >
              {t("authWelcome.signin.link")}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
